import { LearnMoreLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import { PriceListCreateForm } from '../components/builder/price-list-create-form';
import { templateToInitialState, usePriceListTemplate } from '../hooks/use-price-list-templates';
import type { PriceListBuilderState } from '../schemas';

export const PriceListCreatePage = withPageProviders(PriceListCreatePageContent);

function PriceListCreatePageContent() {
  const { navigationHandler } = useHandlers();
  const settings = useSettings();

  const query = navigationHandler.query();
  const template = usePriceListTemplate(query?.template);
  const mode = query?.mode === 'automatic' || query?.mode === 'manual' ? query.mode : undefined;

  // This param (?mode=) pre selects the matching pricing section (the empty state's CTAs pass it).
  // Applying it as draft state counts as an unsaved change, which shows Shopify's contextual
  // save bar.
  const modeDraftState = (m: 'automatic' | 'manual'): PriceListBuilderState => ({
    name: '',
    handle: '',
    currency: settings.baseCurrency,
    automaticPricing: m === 'automatic' ? { priceListSource: '' } : undefined,
    manualPricing: m === 'manual' ? { file: undefined } : undefined,
  });

  // Drafts only apply once, so wait on a pending ?template= rather than letting ?mode= race it
  const draftState = query?.template
    ? template
      ? templateToInitialState(template)
      : undefined
    : mode
      ? modeDraftState(mode)
      : undefined;

  return (
    <Page
      narrow
      title={t('price_list_builder.header.title.create')}
      description={
        <>
          {t('price_list_builder.header.description.create')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/managing-pricing" />
        </>
      }
      backUrl="/pricing/lists"
    >
      <PriceListCreateForm
        draftState={draftState}
        onSuccess={() => navigationHandler.navigate('/pricing/lists')}
        onCancel={() => navigationHandler.navigate('/pricing/lists')}
      />
    </Page>
  );
}
